import type { ToolLibraryEntry, ToolGrant, ToolActivity, ToolGrantInput } from '@platform/shared-types';

// Admin client for tools-service (Internal Tools / Tool Library).
//
// Host shape: tools-service is its OWN REGIONAL API on a per-service
// subdomain — `tools.api.[dev.|stage.]<apex>` — like issues-service
// (consoleApi.ts), NOT a base path on the shared `platform` cluster. On
// localhost the Vite proxy (`/tools-api` -> tools.api.dev.<apex>) stands in.
// Same auth source as every sibling: the admin id token under 'jwtToken',
// sent bare (no "Bearer " prefix).

export type ToolTenantHit = { tenantId: string; name: string; locationCount?: number };

const detectStage = (): 'prod' | 'dev' | 'staging' => {
  if (typeof window === 'undefined') return 'dev';
  try {
    const v = (import.meta as any)?.env?.VITE_STAGE;
    if (v === 'prod' || v === 'staging' || v === 'dev') return v;
  } catch {
    // ignore
  }
  const h = window.location.hostname;
  if (h.startsWith('admin.dev.') || h.includes('localhost') || h.startsWith('127.')) return 'dev';
  if (h.includes('stage')) return 'staging';
  return 'prod';
};

/** Pure host resolver — exported for tests. */
export function resolveToolsHost(
  hostname: string, stage: 'prod' | 'staging' | 'dev', envBase?: string,
): string {
  if (envBase) return envBase;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '/tools-api';
  const apex = (hostname || 'rubytill.com').replace(/^(dev|stage|www|admin\.dev|admin\.stage|admin)\./, '');
  if (stage === 'prod') return `https://tools.api.${apex}`;
  if (stage === 'staging') return `https://tools.api.stage.${apex}`;
  return `https://tools.api.dev.${apex}`;
}

const host = (): string => {
  let envBase: string | undefined;
  try { envBase = (import.meta as any)?.env?.VITE_TOOLS_API_BASE || undefined; } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  return resolveToolsHost(h, detectStage(), envBase);
};

function authHeader(): Record<string, string> {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('jwtToken')) || '';
  return token ? { Authorization: token } : {};
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${host()}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...authHeader(), ...(init.headers || {}) },
  });
  const text = await res.text();
  let body: any = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = null; }
  if (!res.ok) {
    const msg = (body && (body.error || body.message)) || `Request failed (${res.status})`;
    throw new Error(String(msg));
  }
  return body as T;
}

const tool = (key: string) => `/tools/admin/tools/${encodeURIComponent(key)}`;

export const toolsApi = {
  library: () => call<{ tools: ToolLibraryEntry[] }>('/tools/admin/library'),
  toolTenants: (toolKey: string) => call<{ grants: ToolGrant[] }>(`${tool(toolKey)}/tenants`),
  setGrant: (toolKey: string, tenantId: string, input: ToolGrantInput) =>
    call<{ grant: ToolGrant }>(`${tool(toolKey)}/tenants/${encodeURIComponent(tenantId)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  activity: (limit = 200) => call<{ activity: ToolActivity[] }>(`/tools/admin/activity?limit=${limit}`),
  searchTenants: (q: string) =>
    call<{ tenants: ToolTenantHit[] }>(`/tools/admin/tenants/search?q=${encodeURIComponent(q)}`),
};
